import { ExtendedCompleteRiskFields, ExtendedRiskActionFields } from '@pages-clean/risk-assessment/risk-log/constants';
import { IExtendedLibraryControlFields } from '@pages-clean/risk-assessment/risk-log/risk-log.interface';
import { RiskActionType } from 'types/graphql-global-types';
import {
    getLibraryControls_OpraQueries,
    getLibraryRisks_OpraQueries,
    getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk,
    getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_libraryControls_LibraryControl,
    getStudyControls_OpraQueries,
    getStudyControls_OpraQueries_studyControls_StudyControl,
    riskObservations_OpraQueries_riskObservations_RiskObservation_actions_RiskAction,
    riskObservationsRisks_OpraQueries_riskObservations_RiskObservation,
} from '@queries/generated/legacy-risk-assessment.generated';

export function mapActionToFlattenedAction(
    action: riskObservations_OpraQueries_riskObservations_RiskObservation_actions_RiskAction | undefined,
    selectedObservation: riskObservationsRisks_OpraQueries_riskObservations_RiskObservation | undefined,
    actionTypeTranslation: string | undefined,
    riskRecordId: string,
): ExtendedRiskActionFields | undefined {
    if (!selectedObservation || !action) {
        return undefined;
    }
    return {
        ...action,
        observation: selectedObservation.description,
        observationId: selectedObservation.id,
        createdBy: action.createdBy ? `${action.createdBy.firstName} ${action.createdBy.lastName}` : '',
        updatedBy: action.updatedBy ? `${action.updatedBy.firstName} ${action.updatedBy.lastName}` : '',
        owner: action.owner
            ? {
                  name: `${action.owner.firstName} ${action.owner.lastName}`,
                  active: action.owner.active,
                  email: action.owner.email,
              }
            : {
                  name: '',
                  active: true,
                  email: null,
              },
        riskId: selectedObservation?.riskId,
        riskStatement: selectedObservation?.riskStatement,
        ...(action.deleted && action.audit
            ? {
                  reasonForDeletion: action.audit.reason,
                  deletedDate: action.audit.created,
                  deletedBy: `${action.audit.userFirstName} ${action.audit.userLastName}`,
              }
            : {}),
        type: action?.type ? (action?.type !== RiskActionType.NONE ? actionTypeTranslation : undefined) : undefined,
        riskRecordId,
    };
}

export function getAssignableControls({
    studyControls,
    libraryControls,
    libraryRisks,
    studyRisk,
}: {
    studyControls?: getStudyControls_OpraQueries;
    libraryControls?: getLibraryControls_OpraQueries;
    libraryRisks?: getLibraryRisks_OpraQueries;
    studyRisk?: ExtendedCompleteRiskFields;
}): IExtendedLibraryControlFields[] {
    // Find the library controls that can be assigned to the risk.
    let filteredLibraryControls =
        studyControls?.studyControls && libraryControls?.libraryControls
            ? (libraryControls.libraryControls
                  .filter(
                      (
                          libraryControl: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_libraryControls_LibraryControl | null,
                      ) =>
                          // Exclude any library controls already added to the study.
                          !studyControls.studyControls?.find(
                              (studyControl: getStudyControls_OpraQueries_studyControls_StudyControl | null) =>
                                  studyControl && libraryControl && studyControl.libraryControlId === libraryControl.id,
                          ) &&
                          // Exclude any library control that is not active.
                          libraryControl?.active,
                  )
                  .map(
                      (
                          libraryControl: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_libraryControls_LibraryControl | null,
                      ) => ({
                          ...libraryControl,
                          icon: 'book-open',
                          library: true,
                          linked: !!libraryRisks?.libraryRisks
                              ?.find(
                                  (lr: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk | null) =>
                                      lr && studyRisk && lr.id === studyRisk.libraryReferenceId,
                              )
                              ?.libraryControls?.some(
                                  (
                                      lc: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_libraryControls_LibraryControl | null,
                                  ) => lc && libraryControl && lc.id === libraryControl.id,
                              ),
                          type: libraryControl?.controlType,
                      }),
                  ) as unknown as IExtendedLibraryControlFields[])
            : [];

    // Include any study control not associated with the current risk.
    if (studyRisk)
        filteredLibraryControls = filteredLibraryControls.concat(
            studyControls?.studyControls
                ? (studyControls?.studyControls
                      .filter(
                          (studyControl: getStudyControls_OpraQueries_studyControls_StudyControl | null) =>
                              // Exclude any study controls already associated to the risk.
                              (!studyRisk?.controlIds || studyRisk?.controlIds.indexOf(studyControl?.id) < 0) &&
                              // Exclude any study control that is not active.
                              studyControl?.inactiveDate === null,
                      )
                      .map((studyControl: getStudyControls_OpraQueries_studyControls_StudyControl | null) => ({
                          ...studyControl,
                          icon: undefined,
                          library: false,
                          linked: !!libraryRisks?.libraryRisks
                              ?.find(
                                  (lr: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk | null) =>
                                      lr && studyRisk && lr.id === studyRisk.libraryReferenceId,
                              )
                              ?.libraryControls?.some(
                                  (
                                      lc: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_libraryControls_LibraryControl | null,
                                  ) => lc && studyControl && lc.id === studyControl.libraryControlId,
                              ),
                      })) as unknown as IExtendedLibraryControlFields[])
                : [],
        );

    return filteredLibraryControls.sort((a, b) => {
        // Sort by library controls first
        if (a.library !== b.library) {
            return a.library ? -1 : 1;
        }
        // Then by created date (oldest first)
        const aDate = a.created ? new Date(a.created).getTime() : 0;
        const bDate = b.created ? new Date(b.created).getTime() : 0;
        return aDate - bDate;
    }) as IExtendedLibraryControlFields[];
}
